/**
 * TestimonialsSection — ★ 2026-10-19
 * Sezione testimonianze video riutilizzabile per TUTTI i funnel/landing.
 * Fonte unica dei video: lib/testimonials.js (13+ video HCF Group).
 *
 * Temi:
 *  • dark  → emerald/gold (mini-landing Bali/Lombok, funnel IT/ES)
 *  • light → crema/foresta (landing Albania Narea / Three Garden, funnel generico)
 *
 * Mostra `featured` card a vista + pulsante "Guarda altre N" (espande il resto).
 * Ogni card: thumbnail YouTube → play inline (nessun autoplay di default).
 */
import React, { useState } from 'react';
import { PlayCircle, ArrowRight, Quote } from 'lucide-react';
import { testimonialsFor, TESTIMONIALS_FEATURED_COUNT, youtubeThumb, youtubeEmbed } from '../../lib/testimonials';

const COPY = {
  it: {
    kicker: 'Prove, non promesse',
    title: 'La voce di chi ha già verificato',
    subtitle: 'Clienti reali, Verification Pass reali. Nessun attore, nessun copione: solo chi è venuto a vedere con i propri occhi.',
    more: (n) => `Guarda altre ${n} testimonianze`,
    watch: (l) => `Guarda: ${l}`,
    investor: 'Investitore HCF',
  },
  es: {
    kicker: 'Pruebas, no promesas',
    title: 'La voz de quien ya verificó',
    subtitle: 'Clientes reales, Verification Pass reales. Sin actores, sin guion: solo quien vino a verlo con sus propios ojos.',
    more: (n) => `Ver ${n} testimonios más`,
    watch: (l) => `Ver: ${l}`,
    investor: 'Inversor HCF',
  },
  en: {
    kicker: 'Proof, not promises',
    title: 'The voice of those who already verified',
    subtitle: 'Real clients, real Verification Pass. No actors, no script: only people who came to see with their own eyes.',
    more: (n) => `Watch ${n} more testimonials`,
    watch: (l) => `Watch: ${l}`,
    investor: 'HCF Investor',
  },
};

const THEMES = {
  dark: {
    bg: '#081712', ink: '#F4EEE1', soft: '#CFC6B2', accent: '#D6B25E',
    cardBg: 'rgba(12,43,33,0.75)', cardBorder: 'rgba(214,178,94,0.35)', overlay: 'rgba(8,23,18,0.35)',
    btnBg: 'rgba(12,43,33,0.7)', btnBorder: 'rgba(214,178,94,0.6)', btnInk: '#F4EEE1',
  },
  light: {
    bg: '#F6F1E8', ink: '#17201C', soft: '#4F5B55', accent: '#9B3B33',
    cardBg: '#FFFFFF', cardBorder: '#DED5C5', overlay: 'rgba(23,32,28,0.28)',
    btnBg: '#0F3B2E', btnBorder: '#0F3B2E', btnInk: '#FFF7EE',
  },
};

function Card({ v, theme, copy, testIdPrefix }) {
  const [playing, setPlaying] = useState(false);
  const th = THEMES[theme];
  return (
    <div
      className="rounded-2xl overflow-hidden border"
      style={{ borderColor: th.cardBorder, background: th.cardBg }}
      data-testid={`${testIdPrefix}-testimonial-${v.id}`}
    >
      <div className="aspect-video relative bg-black">
        {playing ? (
          <iframe
            src={youtubeEmbed(v.id, { autoplay: true })}
            title={v.label}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group absolute inset-0 w-full"
            aria-label={copy.watch(v.label)}
            data-testid={`${testIdPrefix}-testimonial-play-${v.id}`}
          >
            <img src={youtubeThumb(v.id)} alt={v.label} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <span className="absolute inset-0" style={{ background: th.overlay }} />
            <span className="absolute inset-0 grid place-items-center">
              <span
                className="w-14 h-14 rounded-full grid place-items-center border transition-transform duration-200 group-hover:scale-110"
                style={{ borderColor: th.accent, background: 'rgba(8,23,18,0.7)' }}
              >
                <PlayCircle className="w-8 h-8" style={{ color: th.accent }} strokeWidth={1.2} />
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="px-4 py-3">
        <div className="font-serif text-[15px] leading-snug" style={{ color: th.ink }}>{v.label}</div>
        <div className="mt-1 text-[11px] font-sans tracking-wide" style={{ color: th.accent }} data-testid={`${testIdPrefix}-testimonial-name-${v.id}`}>
          {v.name || copy.investor}
        </div>
      </div>
    </div>
  );
}

export function TestimonialsSection({
  lang = 'it',
  theme = 'dark',
  featured = TESTIMONIALS_FEATURED_COUNT,
  testIdPrefix = 'funnel',
  id = 'testimonianze',
  className = '',
  kicker,
  title,
  subtitle,
}) {
  const [showAll, setShowAll] = useState(false);
  const copy = COPY[lang] || COPY.it;
  const th = THEMES[theme] || THEMES.dark;
  const all = testimonialsFor(lang);
  const visible = showAll ? all : all.slice(0, featured);
  const remaining = all.length - visible.length;

  return (
    <section id={id} className={`relative scroll-mt-16 ${className}`} style={{ background: th.bg }} data-testid={`${testIdPrefix}-testimonials`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-sans tracking-[0.28em] uppercase" style={{ color: th.accent }}>
            <Quote className="w-3.5 h-3.5" /> {kicker || copy.kicker}
          </div>
          <h2 className="mt-3 font-serif text-[2rem] sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight" style={{ color: th.ink }} data-testid={`${testIdPrefix}-testimonials-title`}>
            {title || copy.title}
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed font-sans" style={{ color: th.soft }}>{subtitle || copy.subtitle}</p>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid={`${testIdPrefix}-testimonials-grid`}>
          {visible.map((v) => <Card key={v.id} v={v} theme={theme} copy={copy} testIdPrefix={testIdPrefix} />)}
        </div>

        {remaining > 0 && (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="inline-flex items-center gap-3 rounded-full border px-6 py-3 text-sm font-sans transition-transform duration-150 hover:scale-[1.02]"
              style={{ borderColor: th.btnBorder, background: th.btnBg, color: th.btnInk }}
              data-testid={`${testIdPrefix}-testimonials-show-more`}
            >
              <PlayCircle className="w-5 h-5" style={{ color: theme === 'light' ? '#D6B25E' : th.accent }} strokeWidth={1.4} />
              {copy.more(remaining)}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export default TestimonialsSection;
